import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import FormLayout from '../../../components/Common/FormLayout';
import { formatDateForInput, cleanFormData } from '../../../utils/formatters';

interface EquipmentFormData {
  nome: string;
  codigo: string;
  tipo: string;
  localizacao: string;
  status: 'ativo' | 'inativo' | 'manutencao';
  fabricante?: string;
  numero_serie?: string;
  capacidade?: string;
  ultima_calibracao?: string;
  proxima_calibracao?: string;
  observacoes?: string;
}

const EquipmentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EquipmentFormData>({
    nome: '',
    codigo: '',
    tipo: '',
    localizacao: '',
    status: 'ativo'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (id) {
      loadEquipmentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadEquipmentData = async () => {
    try {
      setIsLoadingData(true);
      const response = await apiService.getEquipment(Number(id)) as ApiResponse<any>;
      
      if (response.success && response.data) {
        const equipment = response.data;
        setFormData({
          // "Código" no formulário corresponde à coluna `tag` no backend
          // (mesmo contrato do cadastro e da listagem).
          nome: equipment.nome || '',
          codigo: equipment.tag || equipment.codigo_patrimonio || '',
          tipo: equipment.tipo || '',
          localizacao: equipment.localizacao || '',
          status: equipment.status || 'ativo',
          fabricante: equipment.fabricante || '',
          numero_serie: equipment.numero_serie || '',
          capacidade: equipment.capacidade || '',
          ultima_calibracao: formatDateForInput(equipment.ultima_calibracao),
          proxima_calibracao: formatDateForInput(equipment.proxima_calibracao),
          // "Observações" no formulário corresponde à coluna `descricao`
          observacoes: equipment.descricao || equipment.observacoes || ''
        });
      } else {
        throw new Error(response.message || 'Erro ao carregar dados do equipamento');
      }
    } catch (error: any) {
      console.error('Erro ao carregar equipamento:', error);
      utils.showNotification(error.message || 'Erro ao carregar dados do equipamento', { type: 'error' });
      navigate('/equipamentos');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name as keyof EquipmentFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.codigo.trim()) {
      newErrors.codigo = 'Código é obrigatório';
    }

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // Limpar dados antes de enviar para a API
      const cleanedData: any = cleanFormData(formData, [
        'fabricante',
        'numero_serie',
        'capacidade',
        'ultima_calibracao',
        'proxima_calibracao',
        'observacoes'
      ]);

      // Mapear os nomes do formulário para as colunas reais do backend:
      // - "Código" (codigo) -> coluna `tag`
      // - "Observações" (observacoes) -> coluna `descricao`
      // As calibrações não possuem coluna correspondente e são descartadas.
      // NÃO enviamos validade/descricao/responsavel como null (isso apagava
      // os valores existentes); ao omiti-los, o update preserva o que já existe.
      const { codigo, observacoes, ultima_calibracao, proxima_calibracao, ...rest } = cleanedData;
      const dataToSend: any = { ...rest, tag: codigo };
      if (observacoes !== undefined) dataToSend.descricao = observacoes;

      const response = await apiService.updateEquipment(Number(id), dataToSend);
      
      if (response.success) {
        utils.showNotification('Equipamento atualizado com sucesso!', { type: 'success' });
        navigate('/equipamentos');
      } else {
        throw new Error(response.message || 'Erro ao atualizar equipamento');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar equipamento:', error);
      utils.showNotification(error.message || 'Erro ao atualizar equipamento', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/equipamentos');
  };

  if (isLoadingData) {
    return (
      <FormLayout
        title="Carregando Equipamento..."
        isLoading={isLoadingData}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando dados do equipamento...</p>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Editar Equipamento"
      entityId={id}
      isLoading={isLoading}
      onSave={handleSubmit}
      onCancel={handleCancel}
    >
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className={`form-control ${errors.nome ? 'error' : ''}`}
                placeholder="Nome do equipamento"
              />
              {errors.nome && <span className="error-message">{errors.nome}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Código *</label>
              <input
                type="text"
                name="codigo"
                value={formData.codigo}
                onChange={handleInputChange}
                className={`form-control ${errors.codigo ? 'error' : ''}`}
                placeholder="Código do equipamento"
              />
              {errors.codigo && <span className="error-message">{errors.codigo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <input
                type="text"
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                className={`form-control ${errors.tipo ? 'error' : ''}`}
                placeholder="Tipo de equipamento"
              />
              {errors.tipo && <span className="error-message">{errors.tipo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Localização</label>
              <input
                type="text"
                name="localizacao"
                value={formData.localizacao}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Localização do equipamento"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="manutencao">Manutenção</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fabricante</label>
              <input
                type="text"
                name="fabricante"
                value={formData.fabricante || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Fabricante do equipamento"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Número de Série</label>
              <input
                type="text"
                name="numero_serie"
                value={formData.numero_serie || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Número de série"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Capacidade</label>
              <input
                type="text"
                name="capacidade"
                value={formData.capacidade || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Capacidade do equipamento"
              />
            </div>
          </div>
        </div>

        {/* Calibração */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Calibração
          </h3>
          
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Última Calibração</label>
              <input
                type="date"
                name="ultima_calibracao"
                value={formData.ultima_calibracao || ''}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Próxima Calibração</label>
              <input
                type="date"
                name="proxima_calibracao"
                value={formData.proxima_calibracao || ''}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>
          
          <div className="form-group">
            <label className="form-label">Observações</label>
            <textarea
              name="observacoes"
              value={formData.observacoes || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Observações adicionais sobre o equipamento"
              rows={4}
            />
          </div>
        </div>
      </form>
    </FormLayout>
  );
};

export default EquipmentEdit;
